// ────────────────────────────────────────────────────────────────────
// Acrostic Notes — a middle-of-the-list-that-actually-spells-the-card
// trick.
//
// Setup during a performance:
//   1) Magician shows the /notes list to the spectator.
//   2) Magician "closes" the phone (navigates to the fake LockScreen).
//   3) Spectator names ANY card.
//   4) Magician taps the flashlight quick-action, then enters the card
//      as their passcode (using the existing (xxx) encoding used by
//      Swiper — a two-digit code like "07" for 7, then the suit letter
//      keyed to 1/2/3/4).
//   5) On unlock, the notes list looks visually identical to before,
//      BUT a run of consecutive titles in the middle of the list now
//      spells the named card by first letters (e.g. 7♥ → SEVENHEARTS
//      → 11 titles: S-E-V-E-N-H-E-A-R-T-S). Each replaced title still
//      reads as a mundane personal note.
//
// This file is the single source of truth for:
//   • the letter → title-bank
//   • the value/suit word tables
//   • the persisted "current armed card" state
//   • the render-time helper `applyAcrosticOverrides()` used by the
//     /notes page to non-destructively rewrite the visible list.
// ────────────────────────────────────────────────────────────────────

import { STORAGE } from "./storage";
import { pickLetterBank } from "./acrosticBanks";
import { localizedAcrosticString } from "./cardNames";

// ─── VALUE / SUIT WORDS ─────────────────────────────────────────────
// The two words are concatenated (no separator) so the acrostic reads
// as a single spelt-out card, e.g. "SEVEN" + "HEARTS" = "SEVENHEARTS".
export const VALUE_WORDS = {
  A:  "ACE",
  "2": "TWO",
  "3": "THREE",
  "4": "FOUR",
  "5": "FIVE",
  "6": "SIX",
  "7": "SEVEN",
  "8": "EIGHT",
  "9": "NINE",
  "10": "TEN",
  T:  "TEN",   // some encodings use "T" for 10
  J:  "JACK",
  Q:  "QUEEN",
  K:  "KING",
};

export const SUIT_WORDS = {
  H: "HEARTS",
  D: "DIAMONDS",
  C: "CLUBS",
  S: "SPADES",
};

// ─── LETTER BANKS ───────────────────────────────────────────────────
// Localized banks live in `./acrosticBanks.js`. This module resolves
// the active bank per render based on the spectator-facing language
// setting so the injected acrostic titles blend naturally into the
// rest of the disguise.
//
// The legacy in-file English bank stays below only as a hardcoded
// safety net if something goes wrong loading the external module.

const LETTER_BANK_LEGACY_EN = {
  A: [
    { title: "Airport pickup — Sat 6pm",     preview: "Terminal 2 arrivals, gate to be confirmed" },
    { title: "Amazon returns box",           preview: "Sneakers, phone case, coffee filter" },
    { title: "Anniversary ideas",            preview: "Restaurant list, weekend break, small gift" },
    { title: "Apartment showing Thursday",   preview: "Bring proof of income + reference" },
    { title: "Apple ID password hint",       preview: "First pet + birth year (again)" },
  ],
  B: [
    { title: "Book club — Thursday 8pm",     preview: "Chapters 4-9, Maya's place" },
    { title: "Birthday shopping",            preview: "Card, wine, small plant" },
    { title: "Bank appointment",             preview: "Bring passport + last 3 payslips" },
    { title: "BBQ Saturday",                 preview: "Buns, salad, extra ice" },
    { title: "Barber Tuesday 5:30",          preview: "Same as last time, a bit shorter" },
  ],
  C: [
    { title: "Coffee with Mia",              preview: "Wednesday, place near her office" },
    { title: "Car service due",              preview: "Booking ref sent to email" },
    { title: "Costco run",                   preview: "Paper towels, olive oil, coffee pods" },
    { title: "Cake for Sunday",              preview: "Chocolate, no icing this time" },
    { title: "Chargers pack list",           preview: "Phone, laptop, watch, headphones" },
  ],
  D: [
    { title: "Dentist Wednesday",            preview: "9:20am — cleaning + panoramic X-ray" },
    { title: "Dry cleaning ready Friday",    preview: "Blue suit + two shirts" },
    { title: "Dinner reservation",           preview: "Table for 4, Saturday 8pm" },
    { title: "Dog walker note",              preview: "Keys under mat, treat jar on counter" },
    { title: "Doctor follow-up",             preview: "Ask about the new prescription" },
  ],
  E: [
    { title: "Email Sam back",               preview: "Re: the invoice from last month" },
    { title: "Errands today",                preview: "Post office, pharmacy, dry cleaner" },
    { title: "Eye exam booked",              preview: "Friday 4pm — bring old glasses" },
    { title: "Electricity bill",             preview: "Due next Tuesday, autopay set up" },
    { title: "Emma's recital",               preview: "Saturday 7pm, wear the navy dress" },
  ],
  F: [
    { title: "Flight home Sunday",           preview: "KL 1471 — window seat, aisle exit" },
    { title: "Farmers market Saturday",      preview: "Sourdough, tomatoes, honey" },
    { title: "Freelance invoice #204",       preview: "Send by end of month, 30-day terms" },
    { title: "Furniture delivery window",    preview: "Between 10am-2pm, needs signature" },
    { title: "Friday movie night",           preview: "Popcorn, the new one Sam mentioned" },
  ],
  G: [
    { title: "Groceries",                    preview: "Milk, eggs, bananas, spinach, tofu" },
    { title: "Gym Tuesday",                  preview: "Legs + core, 45 min max" },
    { title: "Gift for Alex",                preview: "Book he mentioned + gift card" },
    { title: "Garden — spring planting",     preview: "Basil, mint, tomatoes, one chili plant" },
    { title: "Guitar strings replacement",   preview: "Elixir 10s — same as last time" },
  ],
  H: [
    { title: "Haircut Friday 6pm",           preview: "Same length, layers a bit shorter" },
    { title: "Home insurance renewal",       preview: "Compare 3 quotes before Friday" },
    { title: "Holiday plans — August",       preview: "Two weeks, coast preferred" },
    { title: "Hiking Sunday",                preview: "Bring water, layers, sun cream" },
    { title: "Hardware store list",          preview: "Screws, sandpaper, wood glue" },
  ],
  I: [
    { title: "IKEA trip",                    preview: "Shelf, plant pots, meatballs" },
    { title: "Insurance renewal",            preview: "Auto — quote expires in 5 days" },
    { title: "Invoice #204 — follow up",     preview: "Chase Friday if not paid" },
    { title: "Interview prep",               preview: "Company research, 3 questions ready" },
    { title: "Ideas for the pitch deck",     preview: "Story arc, one big number, close" },
  ],
  J: [
    { title: "Jury duty date",               preview: "Downtown courthouse, 8:30am" },
    { title: "Jazz brunch Sunday",           preview: "Reservation for 3, 12:15" },
    { title: "James — wedding gift",         preview: "Registry link + card" },
    { title: "Job application — draft",      preview: "Tailor CV bullets to the ad" },
    { title: "Juice cleanse — day 2",        preview: "Green, red, ginger shot" },
  ],
  K: [
    { title: "Kids school pickup",           preview: "Wednesday early — 2:15 dismissal" },
    { title: "Kitchen renovation quotes",    preview: "Three vendors booked next week" },
    { title: "Karaoke Friday",               preview: "Sam's birthday, book the room" },
    { title: "Kevin's new number",           preview: "Update contact, delete old one" },
    { title: "Knife sharpener — order",      preview: "The one with the two guides" },
  ],
  L: [
    { title: "Laundry Sunday",               preview: "Sheets + towels + gym stuff" },
    { title: "Lunch with mom",               preview: "Wednesday 12:30, place on Main" },
    { title: "Library book due",             preview: "Next Tuesday — renew online if not done" },
    { title: "London trip notes",            preview: "Hotel, transfer, day-by-day rough plan" },
    { title: "License renewal",              preview: "Bring passport + address proof" },
  ],
  M: [
    { title: "Mom's birthday",               preview: "May 14 — flowers + call in the morning" },
    { title: "Meeting notes — Q3 review",    preview: "Numbers up 12%, headcount plan pending" },
    { title: "Movie night ideas",            preview: "The heist one, the doc, the sequel" },
    { title: "Meal prep Sunday",             preview: "Rice, chicken, roasted veg, dressing" },
    { title: "Marathon training week 6",     preview: "Long run Sat, tempo Tue, rest Sun" },
  ],
  N: [
    { title: "New passport application",     preview: "Photo, form, appointment slot" },
    { title: "Netflix picks",                preview: "The Icelandic one, the finance doc" },
    { title: "Neighbours — BBQ Saturday",    preview: "Bring drinks + extra chairs" },
    { title: "Nail appointment",             preview: "Saturday 11, same as before" },
    { title: "Notebook — new one",           preview: "Dotted, A5, soft cover" },
  ],
  O: [
    { title: "Office move — key dates",      preview: "Movers Tuesday, network up by Thu" },
    { title: "Optician follow-up",           preview: "New prescription — order online" },
    { title: "Oil change due",               preview: "Service light on for 200km" },
    { title: "Online course — week 2",       preview: "Two videos, one quiz, essay outline" },
    { title: "Orchestra tickets — Friday",   preview: "Row H, seats 12 & 13" },
  ],
  P: [
    { title: "Packing list — trip",          preview: "Passport, charger, meds, adapter" },
    { title: "Pay rent",                     preview: "Due 1st — set up standing order" },
    { title: "Pilates class",                preview: "Saturday 9am, book by Thursday" },
    { title: "Passport photo appointment",   preview: "Booth on Main St, plain background" },
    { title: "Pharmacy pickup",              preview: "Vitamin D, thyroid script refill" },
  ],
  Q: [
    { title: "Quarterly report — draft",     preview: "Numbers by Wed, deck by Fri" },
    { title: "Quiz night Thursday",          preview: "Team of 4 — need one more" },
    { title: "Quiet weekend plan",           preview: "Books, walk, no phone until Sunday" },
    { title: "Quotes from Jen — proposal",   preview: "Fold into intro paragraph" },
    { title: "Q1 goals check-in",            preview: "Two on track, one slipping" },
  ],
  R: [
    { title: "Rent — due 1st",               preview: "Standing order set, double check" },
    { title: "Recipe: mushroom risotto",     preview: "Chicken stock, parmesan, white wine" },
    { title: "Return the package",           preview: "Wrong size, label already printed" },
    { title: "Renew car insurance",          preview: "Compare 3 quotes, current expires Fri" },
    { title: "Reunion — save the date",      preview: "August weekend, cabin near the lake" },
  ],
  S: [
    { title: "Sunday plans",                 preview: "Brunch, walk, laundry, prep for Mon" },
    { title: "Sourdough — feed the starter", preview: "Every 12 hours until Saturday" },
    { title: "School forms",                 preview: "Sign the trip permission slip" },
    { title: "Sarah's birthday",             preview: "Card, small gift, brunch Sunday" },
    { title: "Sunday brunch reservation",    preview: "Table for 4, 11:30, the corner spot" },
  ],
  T: [
    { title: "Taxes — gather receipts",      preview: "Utilities, freelance, medical, donations" },
    { title: "Trip to Rome — day 1",         preview: "Land 10am, hotel by noon, easy walk" },
    { title: "Tuesday errands",              preview: "Post office, hardware store, groceries" },
    { title: "Therapy — Wednesday 5pm",      preview: "Bring the journal from last time" },
    { title: "Tickets for Friday",           preview: "Row H, printed + on phone" },
  ],
  U: [
    { title: "Uber to airport",              preview: "Book night before — pick up 5:15am" },
    { title: "Utility bill — check meter",   preview: "Estimated seems too high this month" },
    { title: "Umbrella at Sam's",            preview: "The navy one — grab it Friday" },
    { title: "Upgrade phone plan",           preview: "More data, keep same number" },
    { title: "Unpack office boxes",          preview: "Books, decor, cable box — this weekend" },
  ],
  V: [
    { title: "Vet appointment",              preview: "Annual checkup + vaccines" },
    { title: "Vacation packing",             preview: "Layers, swim, walking shoes, book" },
    { title: "Visa application",             preview: "Photo, form, bank statement, letter" },
    { title: "Vinyl shopping — Saturday",    preview: "The reissue, maybe the live one" },
    { title: "Volunteer sign-up",            preview: "Two Saturdays a month, morning shift" },
  ],
  W: [
    { title: "Weekend plans",                preview: "Groceries, run, brunch, laundry, book" },
    { title: "Water bill — autopay?",        preview: "Set it up before this month" },
    { title: "Workout Monday",               preview: "Push day — chest, shoulders, triceps" },
    { title: "Wine pairings — Saturday",     preview: "Two whites, one red, sparkling to start" },
    { title: "Wedding gift — Sam & Alex",    preview: "Registry item, card, small note" },
  ],
  X: [
    { title: "X-ray follow-up",              preview: "Results Wednesday, book the call" },
    { title: "Xmas card list",               preview: "Family, close friends, work — 24 total" },
    { title: "Xfinity — call about the bill",preview: "Charge for a channel we don't have" },
  ],
  Y: [
    { title: "Yoga Tuesday",                 preview: "7am class, bring mat + water" },
    { title: "Yearly checkup",               preview: "Book by end of month, fasting" },
    { title: "Yard cleanup Saturday",        preview: "Rake, bags, gloves, bin day Monday" },
    { title: "Year-end review draft",        preview: "3 wins, 2 misses, one big goal" },
  ],
  Z: [
    { title: "Zoo trip Saturday",            preview: "Meet at 10, bring snacks + water" },
    { title: "Zoom with team",               preview: "Wednesday 3pm — agenda in email" },
    { title: "Zero-waste ideas",             preview: "Bulk bins, cloth bags, reusable bottles" },
  ],
};

// ─── STORAGE ────────────────────────────────────────────────────────
export function isAcrosticEnabled() {
  // Defaults to ENABLED so the trick works out of the box. Magician
  // can disable via settings toggle if they want to be extra safe
  // during a Search-Force performance where a stray unlock might
  // otherwise trigger it.
  const v = localStorage.getItem(STORAGE.ACROSTIC_ENABLED);
  return v === null ? true : v === "1";
}

export function setAcrosticEnabled(enabled) {
  localStorage.setItem(STORAGE.ACROSTIC_ENABLED, enabled ? "1" : "0");
}

/** Persist the armed card. `card` is `{ value, suit }` matching the
 *  Swiper card schema, e.g. `{ value: "7", suit: "H" }`. Pass `null`
 *  or omit to clear. Fires a same-tab event so /notes rerenders. */
export function setAcrosticCard(card) {
  if (!card || !card.value) {
    localStorage.removeItem(STORAGE.ACROSTIC_CARD);
  } else {
    localStorage.setItem(STORAGE.ACROSTIC_CARD, JSON.stringify(card));
  }
  try {
    window.dispatchEvent(new CustomEvent("mr-acrostic-changed"));
  } catch { /* SSR guard */ }
}

export function getAcrosticCard() {
  try {
    const raw = localStorage.getItem(STORAGE.ACROSTIC_CARD);
    if (!raw) return null;
    const c = JSON.parse(raw);
    if (!c || !c.value) return null;
    return c;
  } catch {
    return null;
  }
}

export function clearAcrostic() {
  setAcrosticCard(null);
}

// ─── ACROSTIC BUILDER ───────────────────────────────────────────────
/** Given a card { value, suit } return the target string of first
 *  letters, e.g. { value: "7", suit: "H" } → "SEVENHEARTS". Returns
 *  null when the card can't be encoded (missing value or unknown
 *  value/suit key). */
/** Build the acrostic target string for the given armed card in the
 *  spectator-facing language. Returns null when the card can't be
 *  encoded (missing value or unknown value/suit key).
 *
 *  langCode = null → falls back to English (`SEVENHEARTS`).
 *  langCode = "nl-NL" → Dutch order SUIT+VALUE (`HARTENZEVEN`).
 *  langCode = "de-DE" → German order SUIT+VALUE (`HERZSIEBEN`).
 *
 *  The old VALUE_WORDS + SUIT_WORDS tables stay exported below for
 *  any legacy caller that might still want the raw English spelling.
 */
export function acrosticStringForCard(card, langCode = null) {
  return localizedAcrosticString(card, langCode);
}

/** Pick one bank entry per required letter, avoiding immediate
 *  duplicates within a single run. Deterministic per (card, seed) so
 *  a re-render inside the same session doesn't scramble the titles,
 *  but different seeds (used for the 2nd/3rd repetitions across the
 *  list) yield different phrasings so the three runs don't read as
 *  three carbon copies stacked on top of each other.
 *
 *  The cursor is salted per-letter with coprime constants (31, 17)
 *  so that changing the seed shifts the bank offset for EVERY letter
 *  independently. This survives banks of any size (5, 4, 3 …) — a
 *  naive `cursor += 1` per letter collided modulo bank size when the
 *  seed spacing happened to be a multiple of the bank size (see
 *  iter_57 review). */
function pickTitles(target, seed = 0, langCode = null) {
  const localizedBank = pickLetterBank(langCode);
  const out = [];
  const used = new Set();
  let letterIndex = 0;
  for (const ch of target) {
    const bank = localizedBank[ch] || LETTER_BANK_LEGACY_EN[ch] || [];
    if (bank.length === 0) {
      // Bank gap — fall back to a safe generic that at least starts
      // with the right letter so the trick still works.
      out.push({ title: `${ch}notes`, preview: "" });
      letterIndex += 1;
      continue;
    }
    // Per-letter salted cursor. 31 and 17 are coprime with every
    // bank size we ship (5, 4, 3), guaranteeing that different seeds
    // land on different bank indices for the same letter.
    const cursor = (((seed | 0) * 31 + letterIndex * 17) | 0);
    let pick = null;
    for (let i = 0; i < bank.length; i++) {
      const candidate = bank[((cursor + i) % bank.length + bank.length) % bank.length];
      const key = candidate.title;
      if (!used.has(key)) {
        pick = candidate;
        used.add(key);
        break;
      }
    }
    // If the entire bank was exhausted (should not happen — every
    // letter has 3+ entries) just take whatever was at cursor.
    if (!pick) pick = bank[((cursor % bank.length) + bank.length) % bank.length];
    out.push(pick);
    letterIndex += 1;
  }
  return out;
}

/** Compute the anchor start positions for up to 3 evenly-spread
 *  acrostic runs. Position 0 is always preserved (real note stays on
 *  top). Runs are non-overlapping and never overflow past the end.
 *
 *  With 150+ decoy notes the three anchors sit near the ~1/4, ~1/2,
 *  and ~3/4 marks so the magician can scroll to any third of the
 *  list and still spot the spelt-out card quickly.
 *
 *  When the list is too short to hold 3 non-overlapping runs the
 *  function gracefully degrades to 2 or 1 — never returning fewer
 *  than 1 valid anchor unless the list literally can't fit even a
 *  single run past position 0. */
function computeAnchors(total, N) {
  if (N <= 0 || total <= 1) return [];
  // Max non-overlapping runs we could theoretically fit past pos 0.
  const capacity = Math.floor((total - 1) / N);
  if (capacity <= 0) {
    // List too short for a fully in-bounds run after pos 0 — bail
    // out entirely rather than overwriting position 0, which would
    // violate the "top of list stays authentic" guarantee. This
    // matters on fresh installs where DEFAULT_DECOYS ships a small
    // preset pool; the user's actual deck (150+ notes) never hits
    // this branch.
    return [];
  }
  const desired = Math.min(3, capacity);
  // Evenly spread runs across positions [1 .. total - N]. For desired
  // = 3 the anchors land near ~1/4, ~1/2, ~3/4 of the usable range,
  // which visually reads as "one run per screen" on the spectator's
  // scroll trajectory.
  const usableStart = 1;
  const usableEnd = total - N;
  if (desired === 1) return [Math.max(1, Math.floor((total - N) / 2))];
  const anchors = [];
  const step = (usableEnd - usableStart) / (desired - 1);
  for (let i = 0; i < desired; i++) {
    anchors.push(Math.round(usableStart + step * i));
  }
  // Enforce non-overlap (protects against rounding collisions when
  // the list is barely large enough for `desired` runs).
  for (let i = 1; i < anchors.length; i++) {
    const minStart = anchors[i - 1] + N;
    if (anchors[i] < minStart) anchors[i] = minStart;
  }
  // Clamp the last anchor if rounding pushed it past the end.
  if (anchors[anchors.length - 1] + N > total) {
    anchors[anchors.length - 1] = total - N;
  }
  return anchors.filter((a) => a >= 1 && a + N <= total);
}

/** Non-destructive: given a list of decoy notes, return a NEW list
 *  where up to 3 runs of N notes have had their title + preview
 *  overwritten so the first-letters spell the armed card. Everything
 *  else (ids, timestamps, bodies, folder assignment) is untouched so
 *  the visible layout / dates match what the spectator saw seconds
 *  ago on the pre-lockscreen glance.
 *
 *  Params:
 *    notes    — array of decoy notes (order matters).
 *    card     — { value, suit } armed by the flashlight quick-action.
 *    langCode — BCP-47 language code from Settings (e.g. "nl-BE").
 *               Falls back to English for non-Latin-script languages.
 */
export function applyAcrosticOverrides(notes, card, langCode = null) {
  if (!Array.isArray(notes) || notes.length === 0) return notes;
  const target = acrosticStringForCard(card, langCode);
  if (!target) return notes;
  const firstRunPicks = pickTitles(target, 0, langCode);
  const N = firstRunPicks.length;
  if (N === 0) return notes;

  const total = notes.length;
  const anchors = computeAnchors(total, N);
  if (anchors.length === 0) return notes;

  // Pre-compute picks per anchor. Different seed per anchor keeps the
  // three runs textually distinct while still spelling the same word.
  // Seeds 0/1/2 are enough because pickTitles now salts the cursor
  // per-letter with coprime constants (see pickTitles doc).
  const picksPerAnchor = anchors.map((_, i) =>
    i === 0 ? firstRunPicks : pickTitles(target, i, langCode),
  );

  // Build a fast index → (anchorIdx, offset) lookup so the mapping
  // pass stays O(n) regardless of how many runs we injected.
  const overrideAt = new Map();
  anchors.forEach((start, aIdx) => {
    for (let off = 0; off < N; off++) {
      overrideAt.set(start + off, { aIdx, off });
    }
  });

  return notes.map((note, idx) => {
    const hit = overrideAt.get(idx);
    if (!hit) return note;
    const pick = picksPerAnchor[hit.aIdx][hit.off];
    return {
      ...note,
      title: pick.title,
      preview: pick.preview || note.preview,
      // Marker so DecoyNotes can style / testid these rows if it wants
      // (kept invisible to spectators — no CSS by default).
      __acrostic: true,
    };
  });
}
